/**
 * @ Author: Housefolios
 * @ Create Time: 2025-04-18 18:28:58
 * @ Modified by: Daniel Clark
 * @ Modified time: 2025-04-22 10:31:25
 * @ Description: Display Commercial Property data
 */

import React from 'react'
// Centralized lodash imports - much cleaner!
import { isEmpty } from '@/utils/lodash'
import { useNavigate, useParams } from 'react-router'
import { useQuery } from "@apollo/client/react";
import {
  GET_COMMERCIAL_PORTFOLIOS,
  GET_COMMERCIAL_PROPERTY} from '../Lots/queries'
import {
  Grid,  Typography, Card, CardContent, Box, Divider, Paper, CircularProgress } from '@mui/material'
import DisplayRealeflowData from '../Dialogs/DisplayRealeflowData'
import {
  Business,
  LocationOn,
  Home,
  CalendarToday,
  Square,
  AttachMoney,
  Gavel} from '@mui/icons-material'
import { currencyFormat, numberFormat } from '../../utils/number'

export default function CommercialProperty(props) {
  const { id = '', view = 'Summary' } = useParams()
  let navigate = useNavigate()

  let results = useQuery(GET_COMMERCIAL_PORTFOLIOS)
  const { commercialPortfolios } = results.data || {}
  const portfolioLoading = results.loading
  const portfolioError = results.error

  results = useQuery(GET_COMMERCIAL_PROPERTY, {
    variables: { id }})
  let { commercialProperty = {} } = results.data || {}
  if (!commercialProperty) commercialProperty = {}
  const propertyLoading = results.loading
  const propertyError = results.error

  if (portfolioLoading || propertyLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (portfolioError) return portfolioError.message
  if (propertyError || isEmpty(commercialProperty)) {
    if (propertyError) return propertyError.message
    return null
  }

  const formatSquareFootage = (sqft) => {
    if (!sqft) return 'N/A'
    return `${numberFormat.format(sqft)} sq ft`
  }

  const PropertyInfoItem = ({ icon, label, value  }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        mb: 2
      }}>
      <Box
        sx={{
          mr: 2,
          color: "primary.main"
        }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="subtitle2" color="textSecondary">
          {label}
        </Typography>
        <Typography variant="body1">{value || 'N/A'}</Typography>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, bgcolor: 'primary.light' }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            color: "primary.contrastText"
          }}
        >
          Commercial Property Details
        </Typography>
        <Typography variant="h6" sx={{
          color: "primary.contrastText"
        }}>
          {commercialProperty.address}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{xs: 12, md: 6}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Location Information
              </Typography>
              <PropertyInfoItem
                icon={<LocationOn />}
                label="Address"
                value={`${commercialProperty.address}, ${commercialProperty.city}, ${commercialProperty.state} ${commercialProperty.zip}`}
              />
              <PropertyInfoItem
                icon={<Business />}
                label="County"
                value={commercialProperty.county}
              />
              <PropertyInfoItem
                icon={<Home />}
                label="Ownership"
                value={commercialProperty.ownershipName}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{xs: 12, md: 6}}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Property Details
              </Typography>
              <PropertyInfoItem
                icon={<CalendarToday />}
                label="Year Built"
                value={commercialProperty.yearBuilt}
              />
              <PropertyInfoItem
                icon={<Business />}
                label="Commercial Type"
                value={commercialProperty.commercialType}
              />
              <PropertyInfoItem
                icon={<Square />}
                label="Square Footage"
                value={formatSquareFootage(commercialProperty.sqft)}
              />
              <PropertyInfoItem
                icon={<Square />}
                label="Lot Size"
                value={formatSquareFootage(
                  commercialProperty.lotSize,
                )}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Financial & Legal Information
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{xs: 12, md: 4}}>
                  <PropertyInfoItem
                    icon={<AttachMoney />}
                    label="Property Taxes"
                    value={
                      commercialProperty.propertyTaxes
                        ? currencyFormat.format(
                            commercialProperty.propertyTaxes,
                          )
                        : 'N/A'
                    }
                  />
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                  <PropertyInfoItem
                    icon={<AttachMoney />}
                    label="Assessed Value"
                    value={
                      commercialProperty.assessedValue
                        ? currencyFormat.format(
                            commercialProperty.assessedValue,
                          )
                        : 'N/A'
                    }
                  />
                </Grid>
                <Grid size={{xs: 12, md: 4}}>
                  <PropertyInfoItem
                    icon={<Gavel />}
                    label="Zoning"
                    value={commercialProperty.zoning}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Box sx={{
            mt: 2
          }}>
            <DisplayRealeflowData
              property={commercialProperty}
              button
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
